import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { HOME_INTERIOR, PLOT_RADIUS_MAX, plotRadius, projectStageIndex, terrainOffsetForSeed } from "@tribe/domain";
import { useVillageStore } from "../store";
import { VOXEL_SIZE, VoxelInstanceManager } from "./VoxelInstanceBuffer";
import { SmokeParticleSystem } from "./SmokeSystem";
import { PetalParticleSystem } from "./PetalSystem";
import { WeatherParticleSystem } from "./WeatherSystem";
import { AmbienceSystem } from "./AmbienceSystem";
import { generateFullVoxelWorld } from "./assets/VoxelTerrain";
import { generateWindmillSailVoxels } from "./assets/VoxelProps";
import { generateWatermillWheelVoxels } from "./assets/VoxelCottage";
import { buildTownSquareVoxels } from "./assets/VoxelTownSquare";
import { buildRingTerrain } from "./terrain";
import { VillagerCrowd } from "./villagers";
import { SheepFlock } from "./sheep";
import { PlacedItemsLayer } from "./PlacedItemsLayer";
import { appendPlacementVoxels } from "./placementVoxels";
import { PlacementControls } from "./PlacementControls";
import { PlacementHandles } from "./PlacementHandles";
import { buildVoxelMeshGroup, disposeVoxelMeshGroup } from "./voxelGroup";
import { blendMood, currentMood, type Mood } from "./dayCycle";
import { applyWeather, currentWeather } from "./weather";
import type { VoxelAsset, VoxelData } from "./voxelTypes";

/**
 * Breathing room between a parcel's fence and the nearest puddle, in voxels.
 *
 * Small on purpose. The member asked for water everywhere except inside the yard itself, so this
 * is the width of a dry verge rather than a moat — puddles still gather right up against the
 * perimeter, in the lanes and on the ground between parcels.
 */
const PUDDLE_PARCEL_MARGIN = 1.5;

/** Rotates an asset's voxels about its own origin and offsets them into the world. */
function placeAsset(
  out: VoxelData[],
  voxels: VoxelData[],
  x: number,
  y: number,
  z: number,
  rotation = 0
): void {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  for (const voxel of voxels) {
    out.push({
      ...voxel,
      x: x + voxel.x * cos - voxel.z * sin,
      y: y + voxel.y,
      z: z + voxel.x * sin + voxel.z * cos
    });
  }
}

/**
 * Builds a standalone group of instanced voxels, batched by colour.
/**
 * The village, as one instanced voxel scene.
 *
 * This is deliberately imperative rather than a React scene graph. The world is a handful of
 * very large instanced meshes rebuilt only when the village itself changes, so there is no
 * per-object reconciliation worth paying for — and the camera, the day cycle and the particle
 * systems all want to run against three.js directly.
 */
export function VoxelWorld() {
  const mountRef = useRef<HTMLDivElement>(null);
  const cameraTarget = useVillageStore((state) => state.cameraTarget);
  const selectedPlotId = useVillageStore((state) => state.selectedPlotId);
  const data = useVillageStore((state) => state.data);

  // The scene is rebuilt only when the shape of the village changes: its seed, or which plots
  // are actually lived on. Panning and selecting must never trigger it.
  const seed = data.world.seed;
  const claimedKey = data.world.plots
    .filter((plot) => plot.assignedOwnerId)
    .map((plot) => plot.id)
    .join(",");
  // Moving home changes the building on the parcel, and expanding it re-levels the ground, so
  // both have to rebuild the scene.
  const homeType = data.property.homeType;
  const plotTier = data.property.plotTier;
  // Neighbours arrive with the bootstrap and are otherwise a stable reference, so comparing by
  // identity rebuilds the village exactly when somebody else's parcel has actually changed.
  const neighbours = data.neighbours;
  // The stage, not the progress. Effort arrives constantly over the presence socket and the
  // square only changes shape five times, so rebuilding on progress would rebuild the village
  // every time anybody anywhere finished a quest.
  const projectStage = projectStageIndex(data.project);

  // Placements change far more often than the village does — every nudge of a lawn chair — so they
  // are deliberately absent from the effect above and get their own cheap update path instead.
  const placements = data.property.placements;

  const selectedPlacementId = useVillageStore((state) => state.selectedPlacementId);
  const ambienceVolume = useVillageStore((state) => state.ambienceVolume);
  const interiorPhase = useVillageStore((state) => state.interiorPhase);

  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const placedItemsRef = useRef<PlacedItemsLayer | null>(null);
  const placementControlsRef = useRef<PlacementControls | null>(null);
  const handlesRef = useRef<PlacementHandles | null>(null);
  const ambienceRef = useRef<AmbienceSystem | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  /** Opens or closes the member.s own house, assigned when the scene is built. */
  const showHomeRef = useRef<((opened: boolean) => void) | null>(null);
  /** The fog the day cycle wants, so stepping back outside restores it exactly. */
  const fogBaseRef = useRef<number | null>(null);
  /** The floor of the member.s home in world units, which the ground under it decides. */
  const homeFloorRef = useRef(0);
  const interiorLightRef = useRef<THREE.PointLight | null>(null);
  const interiorLayerRef = useRef<PlacedItemsLayer | null>(null);
  const interiorControlsRef = useRef<PlacementControls | null>(null);
  const desiredTarget = useRef(new THREE.Vector3(0, 2, 0));
  const desiredPosition = useRef(new THREE.Vector3(45, 40, 60));
  const userDriving = useRef(false);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = Math.max(1, container.clientWidth);
    const height = Math.max(1, container.clientHeight);
    const mood = currentMood();

    const scene = new THREE.Scene();
    scene.background = mood.sky.clone();
    scene.fog = new THREE.FogExp2(mood.sky.getHex(), mood.fogDensity);
    fogBaseRef.current = mood.fogDensity;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.copy(desiredPosition.current);
    cameraRef.current = camera;

    const renderer = rendererRef.current = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2 - 0.02;
    controls.minDistance = 5;
    controls.maxDistance = 260;
    controls.target.copy(desiredTarget.current);
    controlsRef.current = controls;

    // A snap must never fight the hand on the mouse: once the member starts dragging, the
    // pending camera move is abandoned rather than played out underneath them.
    controls.addEventListener("start", () => { userDriving.current = true; });
    controls.addEventListener("end", () => {
      userDriving.current = false;
      desiredPosition.current.copy(camera.position);
      desiredTarget.current.copy(controls.target);
    });

    const ambient = new THREE.AmbientLight(mood.ambientColor.getHex(), mood.ambientIntensity);
    scene.add(ambient);
    const hemi = new THREE.HemisphereLight(mood.hemiSky.getHex(), mood.hemiGround.getHex(), mood.hemiIntensity);
    hemi.position.set(0, 50, 0);
    scene.add(hemi);
    const sun = new THREE.DirectionalLight(mood.sunColor.getHex(), mood.sunIntensity);
    sun.position.copy(mood.sunPosition);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 400;
    const shadowSpan = 90;
    sun.shadow.camera.left = -shadowSpan;
    sun.shadow.camera.right = shadowSpan;
    sun.shadow.camera.top = shadowSpan;
    sun.shadow.camera.bottom = -shadowSpan;
    scene.add(sun);

    // The authored village at the centre, identical for every guild.
    const core = generateFullVoxelWorld(terrainOffsetForSeed(seed), 116);
    const claimed = new Set(
      data.world.plots.filter((plot) => plot.assignedOwnerId).map((plot) => plot.id)
    );
    // The only home type we know for certain is the member's own. Everyone else's still comes
    // from the position hash, because the bootstrap carries no property state for neighbours.
    // Every parcel we know the truth about: our own, plus everyone else's as the server reported
    // it. Anything still missing falls back to the position hash, which now only happens for a
    // plot claimed since the bootstrap was fetched.
    const knownHomes = new Map<string, typeof data.property.homeType>([
      [data.property.plotId, data.property.homeType],
      ...data.neighbours.map((neighbour) => [neighbour.plotId, neighbour.homeType] as const)
    ]);
    const knownRadii = new Map<string, number>([
      [data.property.plotId, plotRadius(data.property)],
      ...data.neighbours.map((neighbour) => [neighbour.plotId, plotRadius(neighbour)] as const)
    ]);
    const ring = buildRingTerrain(
      data.world.plots, data.world.paths, terrainOffsetForSeed(seed), claimed, knownHomes, knownRadii
    );

    const all: VoxelData[] = [...core.terrainVoxels, ...core.waterVoxels, ...ring.voxels];
    for (const entry of core.cottageAssets) placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z, entry.rotation);
    for (const entry of core.treeAssets) placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z);
    for (const entry of core.cropAssets) placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z);
    for (const entry of core.propAssets) placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z, entry.rotation ?? 0);
    // The member's own home is kept out of the fused buffer and drawn as its own group, because it
    // is the one building that has to be able to open up. Everything else in the village is baked.
    // One extra draw call for the house you can walk into is a fair trade.
    const myPlot = data.world.plots.find((plot) => plot.id === data.property.plotId);
    const myHome = myPlot
      ? ring.cottages.find(
          (entry) => entry.x === Math.round(myPlot.position.x) && entry.z === Math.round(myPlot.position.z)
        )
      : undefined;
    for (const entry of ring.cottages) {
      if (entry === myHome) continue;
      placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z, entry.rotation);
    }
    for (const entry of ring.trees) placeAsset(all, entry.asset.voxels, entry.x, entry.y, entry.z);

    // The community project, in the village rather than only in a progress ring on a panel.
    all.push(...buildTownSquareVoxels(projectStage, ring.groundAt));

    // Neighbours' gardens. Nobody can pick these up, so they belong in the shared buffers rather
    // than in a group each — a full guild of parcels would otherwise be thousands of draw calls
    // of scenery. Only our own items stay individually addressable, below.
    for (const neighbour of data.neighbours) {
      const plot = data.world.plots.find((candidate) => candidate.id === neighbour.plotId);
      if (!plot || !neighbour.placements.length) continue;
      const surfaceY = ring.groundAt(Math.round(plot.position.x), Math.round(plot.position.z)) + 0.5;
      appendPlacementVoxels(all, neighbour.placements, plot, surfaceY);
    }

    const lights = [...core.homeLightPositions, ...ring.homeLights];
    const instances = new VoxelInstanceManager();
    const { pointLights } = instances.createInstancedWorld(scene, all, lights);

    // The two moving props. Both are baked into the static buffers as bare towers, so their
    // moving parts are built separately and turned every frame.
    const sails = buildVoxelMeshGroup(generateWindmillSailVoxels());
    const windmill = core.propAssets.find((prop) => prop.asset.id === "windmill_1");
    if (windmill) {
      sails.position.set(
        windmill.x * VOXEL_SIZE,
        (windmill.y + 20) * VOXEL_SIZE,
        // Stood off the tower face so the sails never z-fight with the wall behind them.
        (windmill.z + 5.8) * VOXEL_SIZE
      );
      scene.add(sails);
    }

    // The wheel turns on its axle, so it hangs off a pivot carrying the mill's own rotation.
    const wheel = buildVoxelMeshGroup(generateWatermillWheelVoxels());
    const wheelPivot = new THREE.Group();
    wheelPivot.add(wheel);
    const watermill = core.cottageAssets.find(
      (cottage) => /watermill|mill/i.test(cottage.asset.name) || cottage.asset.id === "cottage_3"
    );
    if (watermill) {
      const rotation = watermill.rotation || 0;
      // Five voxels out from the mill's centre, at the wall bracket that carries the axle.
      const armX = 5 * VOXEL_SIZE;
      wheelPivot.position.set(
        watermill.x * VOXEL_SIZE + armX * Math.cos(rotation),
        watermill.y * VOXEL_SIZE + 2 * VOXEL_SIZE,
        watermill.z * VOXEL_SIZE + armX * Math.sin(rotation)
      );
      wheelPivot.rotation.y = -rotation;
      scene.add(wheelPivot);
    }

    // The member's own yard. Its own group, so a move is a matrix write rather than a rebuild of
    // the entire village.
    sceneRef.current = scene;
    /**
     * The member's home, drawn either whole or opened up.
     *
     * Stepping inside does not build a second world — it takes the roof and the front wall off
     * this one. The village behind stays exactly where it was, which is the point: a home is worth
     * decorating because it sits in a street somebody else can walk down.
     */
    let homeGroup: THREE.Group | null = null;
    const showHome = (opened: boolean) => {
      if (!myHome) return;
      const inside = homeType ? HOME_INTERIOR[homeType] : null;
      const source = opened && inside
        // Local coordinates, before the plot's rotation is applied: everything at or above the
        // ceiling is roof, and everything past the near wall is the face we are looking through.
        //
        // The third rule is less obvious. Each window has a full-size gold voxel parked one step
        // behind it whose whole job is to glow through the glass from the street — from outside it
        // is a warm window, from inside it is a metre of solid gold stuck to the wall. It is a
        // lighting trick that only works when there is a wall in front of it, so it comes out with
        // the wall. Small emissive voxels are real fittings — lanterns, embers — and stay.
        ? myHome.asset.voxels.filter((voxel) => {
            // At the ceiling line, not above it. A cottage lays a solid plane across the top of its
            // walls and the roof sits above that, so dropping only the roof left the room lidded —
            // fine to stand in, useless to arrange, because arranging is done from above.
            if (voxel.y >= inside.ceilingY) return false;
            if (voxel.z >= inside.halfZ + 1) return false;
            // Hollow out the room. A cottage is already a shell so this changes nothing there, but
            // a hobbit hole is a solid dome of earth — without this, stepping inside one would put
            // the member and their furniture inside a hill.
            const inTheRoom = voxel.y >= 1 && voxel.y <= inside.ceilingY &&
              Math.abs(voxel.x) < inside.halfX && Math.abs(voxel.z) < inside.halfZ;
            if (inTheRoom) return false;
            const fullSize = (voxel.scaleX ?? voxel.scale ?? 1) >= 1 && (voxel.scaleY ?? voxel.scale ?? 1) >= 1;
            const behindGlass = voxel.materialType === "emissive" && fullSize &&
              Math.abs(voxel.x) <= inside.halfX && Math.abs(voxel.z) <= inside.halfZ;
            return !behindGlass;
          })
        : myHome.asset.voxels;

      const voxels: VoxelData[] = [];
      placeAsset(voxels, source, myHome.x, myHome.y, myHome.z, myHome.rotation);
      if (homeGroup) {
        scene.remove(homeGroup);
        disposeVoxelMeshGroup(homeGroup);
      }
      homeGroup = buildVoxelMeshGroup(voxels);
      scene.add(homeGroup);
    };
    /**
     * Lamplight indoors.
     *
     * The gold voxels behind each window were doing this job by pretending to be light, which only
     * worked from the street. Taking them out of the cutaway left the room genuinely unlit, so the
     * glow becomes an actual light — one the furniture can be lit *by* rather than one made of
     * blocks the member would otherwise have to look at.
     */
    const hearth = new THREE.PointLight(0xffd39a, 0, 16, 1.7);
    hearth.visible = false;
    scene.add(hearth);
    interiorLightRef.current = hearth;
    if (myHome && homeType) {
      const inside = HOME_INTERIOR[homeType];
      hearth.position.set(
        myHome.x * VOXEL_SIZE,
        (myHome.y + inside.ceilingY - 0.5) * VOXEL_SIZE,
        myHome.z * VOXEL_SIZE
      );
    }

    showHome(false);
    showHomeRef.current = showHome;
    // The house sits on levelled ground whose height comes from the terrain, so the camera has to
    // be told where the floor actually is rather than assuming sea level and ending up under it.
    if (myHome) homeFloorRef.current = myHome.y * VOXEL_SIZE;

    const ownPlot = myPlot;
    if (ownPlot) {
      // The pad's top voxel sits at `groundAt`, and a voxel is drawn centred on its coordinate,
      // so its upper face — the ground you actually stand on — is half a voxel above that.
      const surfaceY = ring.groundAt(Math.round(ownPlot.position.x), Math.round(ownPlot.position.z)) + 0.5;
      const layer = new PlacedItemsLayer(scene, { kind: "yard", plot: ownPlot, surfaceY });
      layer.sync(useVillageStore.getState().data.property.placements);
      placedItemsRef.current = layer;

      const handles = new PlacementHandles(scene);
      handlesRef.current = handles;

      placementControlsRef.current = new PlacementControls({
        canvas: renderer.domElement,
        camera,
        controls,
        layer,
        handles,
        onCommit: (placementId, position) => useVillageStore.getState().movePlacement(placementId, position),
        onSelect: (placementId) => useVillageStore.getState().selectPlacement(placementId),
        onRotate: (placementId, quarterTurns) => useVillageStore.getState().rotatePlacement(placementId, quarterTurns),
        onScale: (placementId, scaleStep) => useVillageStore.getState().setPlacementScale(placementId, scaleStep),
        onRemove: (placementId) => useVillageStore.getState().removePlacement(placementId),
        getSelected: () => useVillageStore.getState().selectedPlacementId,
        getPlacements: () => useVillageStore.getState().data.property.placements,
        getPlotTier: () => useVillageStore.getState().data.property.plotTier,
        getHomeType: () => useVillageStore.getState().data.property.homeType
      });
    }

    /**
     * The inside of the home, as a second layer in the same scene.
     *
     * The house is not a different world, so its contents are not a different scene — they are a
     * group standing on its floor, turned with the building. A placement's stored position is
     * home-local voxels, which is exactly what the domain validated, so nothing is converted twice.
     */
    if (myHome) {
      // The cottage's base row of voxels is its floor slab, and a voxel is drawn centred on its
      // coordinate, so what you stand on is half a voxel above it.
      const interiorLayer = new PlacedItemsLayer(scene, {
        kind: "interior",
        origin: { x: myHome.x, y: myHome.y + 0.5, z: myHome.z },
        rotationY: myHome.rotation
      });
      interiorLayer.sync(useVillageStore.getState().data.property.placements);
      interiorLayerRef.current = interiorLayer;

      const interiorControls = new PlacementControls({
        canvas: renderer.domElement,
        camera,
        controls,
        layer: interiorLayer,
        // The same grips the yard uses. One instance, so the two sides cannot drift apart in size,
        // spacing or behaviour — only one item is ever selected, so only one set is ever needed.
        handles: handlesRef.current!,
        onCommit: (placementId, position) => useVillageStore.getState().movePlacement(placementId, position),
        onSelect: (placementId) => useVillageStore.getState().selectPlacement(placementId),
        onRotate: (placementId, quarterTurns) => useVillageStore.getState().rotatePlacement(placementId, quarterTurns),
        onScale: (placementId, scaleStep) => useVillageStore.getState().setPlacementScale(placementId, scaleStep),
        onRemove: (placementId) => useVillageStore.getState().removePlacement(placementId),
        getSelected: () => useVillageStore.getState().selectedPlacementId,
        getPlacements: () => useVillageStore.getState().data.property.placements,
        getPlotTier: () => useVillageStore.getState().data.property.plotTier,
        getHomeType: () => useVillageStore.getState().data.property.homeType
      });
      // Outdoors until told otherwise; the phase effect below owns which side is live.
      interiorControls.setActive(false);
      interiorControlsRef.current = interiorControls;
    }

    const villagers = new VillagerCrowd(
      scene,
      core.characterPaths,
      core.cottageAssets,
      core.propAssets.find((prop) => prop.asset.id === "bridge_1")
    );

    const sheep = new SheepFlock(scene, core.sheepPaths);

    const smoke = new SmokeParticleSystem(scene);
    const petals = new PetalParticleSystem(scene);
    const weatherSystem = new WeatherParticleSystem(scene);
    // Puddles need somewhere to stand and rain needs something to land on, so the weather is
    // given the whole world's ground, members' parcels included — rain, snow and wet stone belong
    // everywhere. Standing water is the one exception: parcels are levelled flat, which is exactly
    // what the puddle scan looks for, so every yard in the guild would otherwise be the wettest
    // ground in the world. The keep-out is the largest a parcel can ever grow to rather than its
    // tier today, so that buying an expansion never uncovers a puddle already inside the fence.
    weatherSystem.setTerrainVoxels(
      [...core.terrainVoxels, ...ring.voxels],
      data.world.plots.map((plot) => ({
        x: plot.position.x,
        z: plot.position.z,
        radius: PLOT_RADIUS_MAX + PUDDLE_PARCEL_MARGIN
      }))
    );
    const chimneys = [...core.chimneyPositions, ...ring.chimneys];
    // The authored village carries its species in the tree's name ("Micro cherry_blossom") and the
    // ring carries it in the id, so both have to be checked — matching on id alone finds nothing.
    const isCherry = (tree: { asset: VoxelAsset }) =>
      /cherry/i.test(tree.asset.id) || /cherry/i.test(tree.asset.name);
    const cherryTrees = [...core.treeAssets, ...ring.trees].filter(isCherry);

    const applyMood = (next: Mood) => {
      scene.background = next.sky.clone();
      if (scene.fog) {
        scene.fog.color.copy(next.sky);
        (scene.fog as THREE.FogExp2).density = next.fogDensity;
      }
      ambient.color.copy(next.ambientColor);
      ambient.intensity = next.ambientIntensity;
      hemi.color.copy(next.hemiSky);
      hemi.groundColor.copy(next.hemiGround);
      hemi.intensity = next.hemiIntensity;
      sun.color.copy(next.sunColor);
      sun.intensity = next.sunIntensity;
      sun.position.copy(next.sunPosition);
      instances.setEmissiveIntensity(next.emissiveIntensity);
      for (const light of pointLights) light.intensity = next.lampIntensity;
    };

    // Weather is a look laid over the clock's mood, and it arrives in a step rather than
    // drifting, so it is crossfaded in. `weatherMix` runs 0 to 1 across the change; the sun
    // intensity is written every frame during it, so the lightning flash has to be re-applied
    // on top rather than left on the light.
    let weather = currentWeather(seed);
    let previousWeather = weather;
    let weatherMix = 1;
    let clearSunIntensity = applyWeather(mood, weather).sunIntensity;

    const applyWeatherMood = () => {
      const base = currentMood();
      const target = applyWeather(base, weather);
      const next = weatherMix >= 1 ? target : blendMood(applyWeather(base, previousWeather), target, weatherMix);
      clearSunIntensity = next.sunIntensity;
      applyMood(next);
    };

    applyMood(applyWeather(mood, weather));
    instances.updateWeatherColors(weather);

    const keys: Record<string, boolean> = {};
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement && /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      keys[event.key.toLowerCase()] = true;
    };
    const onKeyUp = (event: KeyboardEvent) => { keys[event.key.toLowerCase()] = false; };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    let frame = 0;
    let last = performance.now();
    let moodClock = 0;
    let weatherClock = 0;
    const move = new THREE.Vector3();

    const animate = (now: number) => {
      frame = requestAnimationFrame(animate);
      const delta = Math.min(0.05, (now - last) / 1000);
      last = now;

      // The clock only needs checking occasionally; the mood moves over hours.
      moodClock += delta;
      if (moodClock > 30) {
        moodClock = 0;
        applyWeatherMood();
      }

      // Weather is checked far more often than the mood, despite changing far less. The mood
      // drifts, so noticing it late is invisible; weather turns at an hour the forecast has
      // already published, and two members standing together must not disagree about whether it
      // has started raining. Two seconds of a hash is nothing.
      weatherClock += delta;
      if (weatherClock > 2) {
        weatherClock = 0;
        const next = currentWeather(seed);
        if (next !== weather) {
          previousWeather = weather;
          weather = next;
          weatherMix = 0;
          // Wet stone and snow-covered thatch are a repaint of the whole village, so this runs
          // once on the turn rather than through the crossfade.
          instances.updateWeatherColors(weather);
          applyWeatherMood();
        }
      }

      if (weatherMix < 1) {
        weatherMix = Math.min(1, weatherMix + delta / 6);
        applyWeatherMood();
      }

      if (!userDriving.current && camera.position.distanceTo(desiredPosition.current) > 0.1) {
        camera.position.lerp(desiredPosition.current, 0.08);
        controls.target.lerp(desiredTarget.current, 0.08);
      }

      move.set(0, 0, 0);
      const speed = 26 * delta;
      if (keys["w"] || keys["arrowup"]) camera.getWorldDirection(move);
      else if (keys["s"] || keys["arrowdown"]) { camera.getWorldDirection(move); move.negate(); }
      else if (keys["a"] || keys["arrowleft"]) { camera.getWorldDirection(move); move.cross(camera.up).negate(); }
      else if (keys["d"] || keys["arrowright"]) { camera.getWorldDirection(move); move.cross(camera.up); }
      if (move.lengthSq() > 0) {
        move.y = 0;
        move.normalize().multiplyScalar(speed);
        camera.position.add(move);
        controls.target.add(move);
        desiredPosition.current.copy(camera.position);
        desiredTarget.current.copy(controls.target);
      }

      instances.animateWater(now / 1000);
      sails.rotation.z += 0.22 * delta;
      wheel.rotateX(0.4 * delta);
      villagers.update(now, delta);
      sheep.update(now, delta);
      smoke.update(chimneys, delta);
      petals.update(cherryTrees, delta);
      // The rain falls where the member is looking, not on the origin — and across as much ground
      // as they can see. Half the view plane at the distance being looked at, widened because the
      // camera looks down at an angle, so the ground it covers is longer than the plane is wide.
      const viewDistance = camera.position.distanceTo(controls.target);
      const viewHalfHeight = Math.tan((camera.fov * Math.PI) / 360) * viewDistance;
      const viewRadius = Math.max(viewHalfHeight * camera.aspect, viewHalfHeight) * 1.35;
      weatherSystem.update(weather, delta, now / 1000, controls.target, viewRadius);
      // Lightning is a flash on top of whatever the sun is currently set to.
      sun.intensity = clearSunIntensity + weatherSystem.lightningIntensityOffset;

      // Grips are billboards held at a fixed size on screen, so they need the camera every frame.
      handlesRef.current?.update(camera, renderer.domElement.clientHeight);

      controls.update();
      renderer.render(scene, camera);
    };
    frame = requestAnimationFrame(animate);

    if (import.meta.env.DEV) {
      (window as unknown as { villageWorld?: unknown }).villageWorld = {
        scene, camera, renderer, controls,
        voxels: all.length,
        coreVoxels: core.terrainVoxels.length,
        ringVoxels: ring.voxels.length,
        homes: ring.cottages.length,
        placedItems: placedItemsRef.current,
        ambience: ambienceRef,
        weather: weatherSystem
      };
    }

    const onResize = () => {
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      placementControlsRef.current?.destroy();
      placementControlsRef.current = null;
      interiorControlsRef.current?.destroy();
      interiorControlsRef.current = null;
      interiorLayerRef.current?.destroy(scene);
      interiorLayerRef.current = null;
      handlesRef.current?.destroy(scene);
      handlesRef.current = null;
      placedItemsRef.current?.destroy(scene);
      placedItemsRef.current = null;
      sceneRef.current = null;
      villagers.destroy(scene);
      sheep.destroy(scene);
      smoke.destroy(scene);
      petals.destroy(scene);
      weatherSystem.destroy(scene);
      for (const group of [sails, wheelPivot]) {
        scene.remove(group);
        disposeVoxelMeshGroup(group);
      }
      instances.dispose(scene);
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      controlsRef.current = null;
      cameraRef.current = null;
    };
    // The scene is expensive to build; it is rebuilt only when the village itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, claimedKey, homeType, plotTier, neighbours, projectStage]);

  // Yard changes never touch the world. The guard matters in both directions: this can fire
  // before the scene exists on first paint, and again after teardown has nulled the layer.
  useEffect(() => {
    placedItemsRef.current?.sync(placements);
    interiorLayerRef.current?.sync(placements);
  }, [placements]);

  /**
   * The sound of the village, which outlives any particular build of it.
   *
   * Deliberately not part of the scene effect above. Ambience depends on the weather and on which
   * side of a wall the member is standing, and on nothing the renderer owns — mounting it with the
   * scene meant a new audio device, a restarted fade and a fresh download of the same megabyte
   * every time a neighbour's parcel changed.
   */
  useEffect(() => {
    const ambience = new AmbienceSystem(useVillageStore.getState().ambienceVolume);
    ambienceRef.current = ambience;

    const update = () => {
      const phase = useVillageStore.getState().interiorPhase;
      ambience.setScene({
        weather: currentWeather(seed),
        indoors: phase === "open" || phase === "entering"
      });
    };
    update();
    // The same two seconds the scene gives the sky, and for the same reason: the forecast has
    // already published the hour a storm breaks, and two members sitting together must not
    // disagree about whether it has started.
    const timer = window.setInterval(update, 2000);

    return () => {
      window.clearInterval(timer);
      ambience.destroy();
      ambienceRef.current = null;
    };
  }, [seed]);

  // Dragging the volume slider must be heard while it is being dragged, so this is its own effect
  // rather than another passenger on the two-second tick.
  useEffect(() => {
    ambienceRef.current?.setVolume(ambienceVolume);
  }, [ambienceVolume]);

  /**
   * Stepping inside, and the phase machine that says when.
   *
   * `setInteriorPhase` had no caller until this existed, which is why "Step inside" used to move
   * the camera and then stop: the store went to `entering` and nothing ever advanced it.
   *
   * Nothing is built or torn down here. Going inside takes the roof and the near wall off the
   * member's own house and walks the camera up to the opening; the village carries on behind it.
   * That is the whole difference between decorating a room and decorating *your house on a street*,
   * and it is why the interior is not a separate scene.
   */
  useEffect(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const scene = sceneRef.current;
    const showHome = showHomeRef.current;
    if (!camera || !controls || !scene || !showHome) return;

    const inside = interiorPhase === "entering" || interiorPhase === "open";
    showHome(inside);

    // Lit only while the roof is off; outdoors the sun does the work and a lamp inside a sealed
    // house would leak through the walls onto the street.
    const hearth = interiorLightRef.current;
    if (hearth) {
      hearth.visible = inside;
      hearth.intensity = inside ? 22 : 0;
    }

    // Only one side of the walls is being arranged at a time. Both sets of controls listen on the
    // same canvas, so the inactive one has to stand down or a click indoors would also land in the
    // garden behind it.
    placementControlsRef.current?.setActive(!inside);
    interiorControlsRef.current?.setActive(inside);
    if (inside) {
      placedItemsRef.current?.setSelected(null);
    } else {
      interiorLayerRef.current?.setSelected(null);
    }

    const plot = useVillageStore.getState().data.world.plots.find(
      (candidate) => candidate.id === useVillageStore.getState().data.property.plotId
    );

    if (inside && plot) {
      // Square to the opening and low enough to see the floor. The near wall is the +Z face in the
      // home's own frame, so the camera stands off along that face, turned by the plot's rotation.
      const bounds = homeType ? HOME_INTERIOR[homeType] : { halfZ: 4, ceilingY: 4 };
      const away = (bounds.halfZ + 7) * VOXEL_SIZE;
      const lift = (bounds.ceilingY * 0.9) * VOXEL_SIZE;
      const facing = -plot.rotationY;
      const floor = homeFloorRef.current;
      desiredTarget.current.set(
        plot.position.x * VOXEL_SIZE,
        floor + (bounds.ceilingY / 2.5) * VOXEL_SIZE,
        plot.position.z * VOXEL_SIZE
      );
      desiredPosition.current.set(
        plot.position.x * VOXEL_SIZE + Math.sin(facing) * away,
        desiredTarget.current.y + lift,
        plot.position.z * VOXEL_SIZE + Math.cos(facing) * away
      );
      // Close quarters: the village limits would let the member back out through their own wall.
      controls.minDistance = 3;
      controls.maxDistance = 26;

      if (interiorPhase === "entering") useVillageStore.getState().setInteriorPhase("open");
    }

    if (interiorPhase === "exiting") {
      controls.minDistance = 5;
      controls.maxDistance = 260;
      if (plot) {
        desiredTarget.current.set(plot.position.x * VOXEL_SIZE, homeFloorRef.current + 2, plot.position.z * VOXEL_SIZE);
        desiredPosition.current.set(
          plot.position.x * VOXEL_SIZE + 18,
          16,
          plot.position.z * VOXEL_SIZE + 26
        );
      }
      useVillageStore.getState().setInteriorPhase("closed");
    }
  }, [interiorPhase, homeType]);

  /**
   * Everything but the house falls back while the member is inside it.
   *
   * A real depth-of-field pass would mean a post-processing pipeline over a scene that currently
   * renders in one call, which is a lot of machinery for one moment. Thickening the fog does the
   * same job with the tools already in the scene: the street softens and drops away, the house
   * stays sharp because it is the near thing, and the village is still visibly there.
   */
  useEffect(() => {
    const scene = sceneRef.current;
    const fog = scene?.fog as THREE.FogExp2 | undefined;
    if (!fog) return;
    const inside = interiorPhase === "entering" || interiorPhase === "open";
    const base = fogBaseRef.current;
    if (base === null) return;
    fog.density = inside ? base * 5.5 : base;
    return () => { fog.density = base; };
  }, [interiorPhase]);

  // Holding an item pins the view. Orbiting and dragging want the same one-finger gesture, so
  // sharing it would mean every attempt to move a bench swung the whole village instead.
  useEffect(() => {
    const inside = interiorPhase === "entering" || interiorPhase === "open";
    if (inside) interiorLayerRef.current?.setSelected(selectedPlacementId);
    else placedItemsRef.current?.setSelected(selectedPlacementId);
    // Both are told, because the camera is shared: whichever set of controls owns the gesture,
    // the view has to hold still while something is in hand.
    placementControlsRef.current?.setCameraLocked(Boolean(selectedPlacementId));
    interiorControlsRef.current?.setCameraLocked(Boolean(selectedPlacementId));
  }, [selectedPlacementId, interiorPhase]);

  // The grips ride the selected item, so they follow it as it is moved, turned or resized. Which
  // set of grips depends on which side of the walls the member is standing on.
  useEffect(() => {
    const inside = interiorPhase === "entering" || interiorPhase === "open";
    const handles = handlesRef.current;
    const layer = inside ? interiorLayerRef.current : placedItemsRef.current;
    if (!handles || !layer) return;
    if (!selectedPlacementId) {
      handles.hide();
      return;
    }
    const anchor = layer.anchorFor(selectedPlacementId);
    if (anchor) handles.show(anchor.position, anchor.radius);
    else handles.hide();
  }, [selectedPlacementId, placements, interiorPhase]);

  // Selecting a plot flies the camera to it. Without a selection the view stays where it
  // opened — on the village as a whole — rather than diving at the origin on mount.
  useEffect(() => {
    if (!selectedPlotId) return;
    const x = cameraTarget.x * VOXEL_SIZE;
    const z = cameraTarget.z * VOXEL_SIZE;
    desiredTarget.current.set(x, 2, z);
    desiredPosition.current.set(x + 18, 16, z + 26);
  }, [selectedPlotId, cameraTarget.x, cameraTarget.z]);

  return <div className="world-canvas" ref={mountRef} />;
}
